/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Table, Button, Form, Badge,
} from 'react-bootstrap';
import { publicDriverService } from '../publicFleetServices';
import FormControlFeedback from '../../../Components/FormControlFeedback';
import setStateFromInput from '../../../Utils/setStateFromInput';
import { driverNameErrors, hasDriverName } from '../../../Utils/driverName';

/**
 * The operator's drivers: list, add, edit, deactivate.
 *
 * `service` is `{ list(), save(record), remove(id) }`. The public operator
 * page passes nothing and gets the link-based calls scoped by `operatorId`
 * (publicFleetServices.js); the signed-in operator portal passes its
 * authenticated service (OperatorPortalApi.js) and no operator id at all.
 *
 * Optional, for the operator portal (the public page passes neither):
 *  - `extraColumn` `{ header, render(driver) }` — one more column per row
 *    (the portal's "Portal access").
 *  - `onDriversLoaded(drivers)` — the list, each time it is (re)loaded.
 */
function DriverManagementSection({
  operatorId, setError, service, extraColumn, onDriversLoaded,
}) {
  const api = useMemo(
    () => service || publicDriverService(operatorId),
    [service, operatorId],
  );
  const enabled = Boolean(service || operatorId);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingDriverId, setEditingDriverId] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
    active: 1,
  });

  const fetchDrivers = async () => {
    if (!enabled) return;

    setLoading(true);
    try {
      const list = await api.list();
      setDrivers(list);
      if (onDriversLoaded) onDriversLoaded(list);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch drivers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (enabled) {
      fetchDrivers();
    }
  }, [api]);

  const handleChange = (e) => {
    setStateFromInput(e, setFormData);
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      phone_number: '',
      active: 1,
    });
    setValidationErrors([]);
  };

  const handleAddDriver = async () => {
    // Both halves, said here as well as by the API: a driver with only one
    // of them is what Booking.com refuses, and the operator is the person
    // who knows the other half.
    const missing = driverNameErrors(formData);

    if (missing.length) {
      setValidationErrors(missing);
      return;
    }

    setLoading(true);
    try {
      await api.save({
        ...formData,
        active: Number(formData.active),
      });
      await fetchDrivers();
      setShowAddForm(false);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add driver');
      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDriver = async (driver) => {
    const missing = driverNameErrors(driver);

    if (missing.length) {
      setValidationErrors(missing);
      return;
    }

    setLoading(true);
    try {
      await api.save({
        Id: driver.Id,
        first_name: driver.first_name,
        last_name: driver.last_name,
        phone_number: driver.phone_number,
        active: Number(driver.active),
      });
      await fetchDrivers();
      setEditingDriverId(null);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update driver');
      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDriver = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to deactivate this driver?')) {
      return;
    }

    setLoading(true);
    try {
      await api.remove(id);
      await fetchDrivers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete driver');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (driver) => {
    setEditingDriverId(driver.Id);
  };

  const cancelEditing = () => {
    setEditingDriverId(null);
    fetchDrivers();
  };

  const handleEditChange = (driverId, field, value) => {
    setDrivers((prevDrivers) => prevDrivers.map((d) => (
      d.Id === driverId ? { ...d, [field]: value } : d
    )));
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="h5 mb-0">Your Drivers</h2>
        {!showAddForm && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowAddForm(true)}
            disabled={loading}
          >
            <i className="fa fa-plus me-2" />
            Add New Driver
          </Button>
        )}
      </div>

      {showAddForm && (
        <div className="card mb-3 bg-light">
          <div className="card-body">
            <h6 className="card-title">Add New Driver</h6>
            <Form>
              <div className="row">
                <Form.Group className="mb-2 col-md-3" controlId="first_name">
                  <Form.Label>First Name *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Péter"
                    value={formData.first_name}
                    onChange={handleChange}
                    size="sm"
                  />
                  <FormControlFeedback errors={validationErrors} field="first_name" />
                </Form.Group>

                <Form.Group className="mb-2 col-md-3" controlId="last_name">
                  <Form.Label>Last Name *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Kovács"
                    value={formData.last_name}
                    onChange={handleChange}
                    size="sm"
                  />
                  <FormControlFeedback errors={validationErrors} field="last_name" />
                </Form.Group>

                <Form.Group className="mb-2 col-md-3" controlId="phone_number">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.phone_number}
                    onChange={handleChange}
                    size="sm"
                  />
                  <FormControlFeedback errors={validationErrors} field="phone_number" />
                </Form.Group>

                <Form.Group className="mb-2 col-md-3" controlId="active">
                  <Form.Label>Active *</Form.Label>
                  <Form.Select
                    value={formData.active}
                    onChange={handleChange}
                    size="sm"
                  >
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                  </Form.Select>
                </Form.Group>
              </div>

              <div className="d-flex gap-2">
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleAddDriver}
                  disabled={loading || !hasDriverName(formData)}
                >
                  <i className="fa fa-check me-2" />
                  Save
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setShowAddForm(false);
                    resetForm();
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </div>
        </div>
      )}

      <Table striped bordered hover size="sm" responsive className="fleet-driver-table">
        <thead>
          <tr>
            <th>Full Name</th>
            <th>Phone Number</th>
            <th>Active</th>
            {extraColumn ? <th>{extraColumn.header}</th> : null}
            <th style={{ width: '150px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {drivers.length === 0 && (
            <tr>
              <td colSpan={extraColumn ? 5 : 4} className="text-center text-muted">
                No drivers found. Add your first driver above.
              </td>
            </tr>
          )}
          {drivers.map((driver) => (
            <tr
              key={driver.Id}
              className={driver.active === 0 ? 'text-muted' : ''}
            >
              {editingDriverId === driver.Id ? (
                <>
                  {/* One column, two boxes: the list shows the composed name,
                      but what is EDITED is the two halves the partner gets. */}
                  <td data-label="Name">
                    <div className="d-flex gap-1">
                      <Form.Control
                        type="text"
                        aria-label={`First name of ${driver.full_name}`}
                        placeholder="First name"
                        value={driver.first_name || ''}
                        onChange={(e) => handleEditChange(driver.Id, 'first_name', e.target.value)}
                        size="sm"
                      />
                      <Form.Control
                        type="text"
                        aria-label={`Last name of ${driver.full_name}`}
                        placeholder="Last name"
                        value={driver.last_name || ''}
                        onChange={(e) => handleEditChange(driver.Id, 'last_name', e.target.value)}
                        size="sm"
                      />
                    </div>
                    <FormControlFeedback errors={validationErrors} field="first_name" />
                    <FormControlFeedback errors={validationErrors} field="last_name" />
                  </td>
                  <td data-label="Phone">
                    <Form.Control
                      type="text"
                      value={driver.phone_number || ''}
                      onChange={(e) => handleEditChange(driver.Id, 'phone_number', e.target.value)}
                      size="sm"
                    />
                  </td>
                  <td data-label="Active">
                    <Form.Select
                      value={driver.active}
                      onChange={(e) => handleEditChange(driver.Id, 'active', Number(e.target.value))}
                      size="sm"
                    >
                      <option value="1">Yes</option>
                      <option value="0">No</option>
                    </Form.Select>
                  </td>
                  {extraColumn ? (
                    <td data-label={extraColumn.header}>{extraColumn.render(driver)}</td>
                  ) : null}
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        variant="success"
                        size="sm"
                        aria-label="Save driver"
                        onClick={() => handleUpdateDriver(driver)}
                        disabled={loading || !hasDriverName(driver)}
                      >
                        <i className="fa fa-check" aria-hidden="true" />
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        aria-label="Cancel editing"
                        onClick={cancelEditing}
                        disabled={loading}
                      >
                        <i className="fa fa-times" aria-hidden="true" />
                      </Button>
                    </div>
                  </td>
                </>
              ) : (
                <>
                  <td data-label="Name">
                    {driver.full_name}
                    {hasDriverName(driver) ? null : (
                      <div className="text-danger small">Needs a first and a last name</div>
                    )}
                  </td>
                  <td data-label="Phone">{driver.phone_number || '-'}</td>
                  <td data-label="Active">
                    {driver.active === 1 ? (
                      <Badge bg="success">Active</Badge>
                    ) : (
                      <Badge bg="danger">Inactive</Badge>
                    )}
                  </td>
                  {extraColumn ? (
                    <td data-label={extraColumn.header}>{extraColumn.render(driver)}</td>
                  ) : null}
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        variant="primary"
                        size="sm"
                        aria-label={`Edit driver ${driver.full_name}`}
                        onClick={() => startEditing(driver)}
                        disabled={loading}
                      >
                        <i className="fa fa-edit" aria-hidden="true" />
                      </Button>
                      {driver.active === 1 && (
                        <Button
                          variant="danger"
                          size="sm"
                          aria-label={`Deactivate driver ${driver.full_name}`}
                          onClick={() => handleDeleteDriver(driver.Id)}
                          disabled={loading}
                        >
                          <i className="fa fa-trash" aria-hidden="true" />
                        </Button>
                      )}
                    </div>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}

export default DriverManagementSection;
